/**
 * Cocinaworks — cart drawer "You may also like" cross-sell rail.
 *
 * Why this lives in a global asset instead of an inline <script> in
 * snippets/cw-cart-xsell.liquid:
 *
 *   1. The cross-sell snippet is only rendered when the cart is NOT empty
 *      (see snippets/header-actions.liquid, the `{% if cart.empty? %}` branch).
 *      So on a first visit with an empty cart the inline <script> that defines
 *      the element is never emitted into the page at all.
 *
 *   2. Even when it is emitted, the cart drawer is swapped into the page with a
 *      DOM morph on every cart change (assets/morph.js / component-cart-items.js).
 *      Browsers do not execute <script> elements that are introduced by
 *      innerHTML or by morphing — only scripts present in the initial HTML parse
 *      run. (morph.js only re-runs `.shopify-app-block script[src]`, not inline
 *      theme scripts.)
 *
 * Together these meant that after an add-to-cart the <cw-cart-xsell> element was
 * inserted but never upgraded: connectedCallback never fired, the delegated
 * click/submit handlers were never bound, and the arrows + Add button did
 * nothing — until a full page reload put the definition back into the initial
 * HTML. Loading the definition globally here (from snippets/scripts.liquid, on
 * every page) fixes that: the element is always defined, and the document-level
 * listeners are always bound, independent of cart state.
 */

// Delegated once, at the document level, so a single binding survives every
// morph of the cart drawer. These handlers are pure DOM delegation and do not
// depend on the custom element being upgraded, so the arrows and Add button
// work on the very first add-to-cart.
if (!window.__cwXsellBound) {
  window.__cwXsellBound = true;

  // Arrows: scroll the horizontal rail one card at a time.
  document.addEventListener('click', function (e) {
    var arrow = e.target.closest('[data-cw-xs]');
    if (!arrow) return;
    var rail = arrow.closest('.cw-xs');
    var track = rail && rail.querySelector('.cw-xs__body');
    var row = track && track.querySelector('.cw-xs__row');
    if (!row) return;
    track.scrollBy({
      left: Number(arrow.dataset.cwXs) * (row.getBoundingClientRect().width + 8),
      behavior: 'smooth',
    });
  });

  // Add: add the item without leaving the drawer, then let the theme re-render
  // the cart by dispatching its cart-update event ('cart:update').
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('.cw-xs__form');
    if (!form) return;
    e.preventDefault();
    var button = form.querySelector('.cw-xs__add');
    var label = button.textContent;
    button.disabled = true;

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(form.querySelector('[name="id"]').value), quantity: 1 }] }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        button.textContent = '✓';
        ['cart:update', 'CartUpdateEvent', 'cartUpdate'].forEach(function (name) {
          document.dispatchEvent(new CustomEvent(name, { detail: data, bubbles: true }));
        });
      })
      .catch(function () {
        button.textContent = '!';
      })
      .finally(function () {
        setTimeout(function () {
          button.disabled = false;
          button.textContent = label;
        }, 1400);
      });
  });
}

if (!customElements.get('cw-cart-xsell')) {
  customElements.define(
    'cw-cart-xsell',
    class extends HTMLElement {
      connectedCallback() {
        this.body = this.querySelector('[ref="body"]');
        if (this.body && this.dataset.product) this.load();
      }

      load() {
        var url =
          this.dataset.endpoint +
          '?section_id=cw-cart-recommendations' +
          '&product_id=' +
          encodeURIComponent(this.dataset.product) +
          '&limit=' +
          encodeURIComponent(this.dataset.limit) +
          '&intent=' +
          encodeURIComponent(this.dataset.intent);

        var self = this;
        fetch(url)
          .then(function (r) {
            return r.ok ? r.text() : Promise.reject(r.status);
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');

            /* A card is recognised by the product link inside it rather than by
               a class name, so a change to the theme's markup cannot quietly
               make this render nothing. */
            var items = Array.from(doc.querySelectorAll('.cw-xs__row')).filter(function (el) {
              return el.querySelector('a[href*="/products/"]');
            });
            if (!items.length) return; /* keep the Liquid fallback */

            var inCart = (self.dataset.inCart || '').split(/\s+/).filter(Boolean);
            var show = parseInt(self.dataset.show, 10) || 4;
            var kept = 0;

            items.forEach(function (el) {
              var handle =
                el.dataset.handle ||
                ((el.querySelector('a[href*="/products/"]').getAttribute('href') || '').split('/products/')[1] || '').split(
                  /[?#]/
                )[0];
              if (kept >= show || inCart.indexOf(handle) !== -1) {
                el.remove();
                return;
              }
              kept++;
            });
            if (!kept) return;

            self.body.innerHTML = '';
            items.forEach(function (el) {
              self.body.appendChild(el);
            });
          })
          .catch(function () {
            /* the fallback is already on screen */
          });
      }
    }
  );
}
